"use client";

import { HugeiconsIcon } from "@hugeicons/react";
import ArrowRight02Icon from "@hugeicons/core-free-icons/ArrowRight02Icon";
import CreditCardNotAcceptIcon from "@hugeicons/core-free-icons/CreditCardNotAcceptIcon";
import { siteContent } from "@/lib/site-content";
import { useAuth } from "@/components/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { MetaFlipBadge } from "@/components/LandingPage/meta-flip-badge";
import { PhoneMockup } from "@/components/LandingPage/phone-mockup";
import { useAnalytics } from "@/lib/analytics";
import { authClient } from "@/lib/better-auth";
import { cn } from "@/lib/utils";
import { useColorTheme } from "@/components/providers/color-theme-provider";

const getPersonalizedCtaText = (name: string | null | undefined) => {
  const firstName = name?.trim().split(/\s+/)[0];
  return `Hello ${firstName || "User"}`;
};

export default function Hero2() {
  const { openAuth } = useAuth();
  const { track } = useAnalytics();
  const { themeColor } = useColorTheme();
  const { data: session } = authClient.useSession();
  const ctaLabel = session?.user
    ? getPersonalizedCtaText(session.user.name)
    : siteContent.hero.ctaPrimary;

  return (
    <section
      id="hero"
      className={cn(
        "relative w-full min-w-0 overflow-x-clip",
        "bg-white dark:bg-black",
        "pt-[6.5rem] sm:pt-[7rem] md:pt-[7.5rem] pb-12 sm:pb-16 md:pb-20",
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55] dark:opacity-[0.18]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(160,106,249,0.06) 0px, rgba(160,106,249,0.06) 2px, transparent 2px, transparent 22px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[min(60vh,520px)] bg-[radial-gradient(ellipse_70%_45%_at_50%_0%,rgba(160,106,249,0.10),transparent)] dark:bg-[radial-gradient(ellipse_70%_45%_at_50%_0%,rgba(160,106,249,0.08),transparent)]"
      />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 lg:px-8">
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          <div className="mb-6 sm:mb-7">
            <MetaFlipBadge className="-rotate-2" />
          </div>

          <h1 className="text-[clamp(2.2rem,7.8vw,4rem)] font-extrabold leading-[1.18] tracking-tight text-foreground sm:leading-[1.05]">
            {siteContent.hero.headingPrefix}
            <span
              className={cn(
                "relative mx-0.5 inline-block align-baseline -rotate-[2deg]",
                "border-2 border-foreground/90 bg-foreground px-[0.22em] py-[0.04em] text-background",
                "shadow-[0_6px_0_-2px_rgba(0,0,0,0.18)]",
              )}
            >
              {siteContent.hero.headingEmphasis}
            </span>
            {siteContent.hero.headingMid}
          </h1>

          <p className="mx-auto mt-5 max-w-[36ch] text-[15px] leading-relaxed text-muted-foreground sm:mt-6 sm:max-w-[46ch] sm:text-[17px] lg:mx-0">
            {siteContent.hero.description}
          </p>

          <div className="mt-7 flex w-full flex-row items-center justify-center gap-2.5 sm:mt-8 sm:gap-3 lg:justify-start">
            <Button
              size="lg"
              onClick={() => {
                track("cta_clicked", {
                  cta_id: "CTA_Hero",
                  button_text: ctaLabel,
                });
                openAuth();
              }}
              className={cn(
                "h-12 min-w-0 w-full max-w-[240px] flex-none rounded-full px-3 text-[15px] font-bold shadow-md shadow-primary/20 sm:w-auto sm:max-w-none sm:px-14 sm:text-base",
                themeColor === "yellow" && "!text-black"
              )}
            >
              {ctaLabel}
              <HugeiconsIcon
                icon={ArrowRight02Icon}
                size={17}
                color="currentColor"
                className="ml-0.5"
              />
            </Button>
          </div>

          <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
            <HugeiconsIcon
              icon={CreditCardNotAcceptIcon}
              size={14}
              color="currentColor"
              className="text-primary/80"
            />
            {siteContent.hero.trustMicrocopy}
          </p>
        </div>

        <div className="relative flex w-full items-center justify-center lg:justify-end lg:pl-4">
          <div className="relative w-full max-w-[340px] lg:max-w-[380px]">
            <PhoneMockup className="animate-float-soft w-full" />
          </div>
        </div>
      </div>
    </section>
  );
}
